import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  marketplaceBrainApi,
  type BusinessContextSnapshot,
  type ConversationResponse,
  type ExecutiveDashboardSnapshot,
  type ExecutiveReport,
  type KnowledgeNode,
  type LearningUpdate,
  type MOBOpportunity,
  type MOBTimelineEvent,
  type OrchestratedRecommendation,
  type ReasoningExplanation,
} from '../../api/marketplace-brain-dev110-runtime';

type Theme = 'light' | 'dark';
type Tab = 'dashboard' | 'recommendations' | 'brief' | 'conversation' | 'opportunities' | 'context' | 'learning' | 'knowledge' | 'timeline';

export function MarketplaceOperatingBrainPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<ExecutiveDashboardSnapshot | null>(null);
  const [context, setContext] = useState<BusinessContextSnapshot | null>(null);
  const [recommendations, setRecommendations] = useState<OrchestratedRecommendation[]>([]);
  const [opportunities, setOpportunities] = useState<MOBOpportunity[]>([]);
  const [brief, setBrief] = useState<ExecutiveReport | null>(null);
  const [conversation, setConversation] = useState<ConversationResponse | null>(null);
  const [reasoning, setReasoning] = useState<ReasoningExplanation | null>(null);
  const [learning, setLearning] = useState<LearningUpdate[]>([]);
  const [knowledge, setKnowledge] = useState<KnowledgeNode[]>([]);
  const [timeline, setTimeline] = useState<MOBTimelineEvent[]>([]);
  const [query, setQuery] = useState('What should I focus on today?');
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = dark ? 'border-indigo-900/40 bg-zinc-900' : 'border-indigo-200 bg-white';
  const muted = dark ? 'text-zinc-400' : 'text-slate-500';
  const shell = dark ? 'bg-zinc-950 text-zinc-100' : 'bg-indigo-50/20 text-slate-900';
  const accent = dark ? 'text-indigo-300' : 'text-indigo-700';
  const tabActive = 'bg-indigo-600 text-white';

  const refresh = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const [dash, ctx, recs, opps, br, learn, know, tl] = await Promise.all([
        marketplaceBrainApi.dashboard(), marketplaceBrainApi.context(),
        marketplaceBrainApi.recommendations(), marketplaceBrainApi.opportunities(),
        marketplaceBrainApi.report('morning_brief'), marketplaceBrainApi.learning(),
        marketplaceBrainApi.knowledge(), marketplaceBrainApi.history(),
      ]);
      setDashboard(dash); setContext(ctx); setRecommendations(recs); setOpportunities(opps);
      setBrief(br); setLearning(learn); setKnowledge(know); setTimeline(tl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Marketplace Operating Brain');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const ask = async () => {
    setAsking(true); setConversation(null);
    try { setConversation(await marketplaceBrainApi.conversation(query)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Conversation failed'); }
    finally { setAsking(false); }
  };

  const showReasoning = async (id: string) => {
    try { setReasoning(await marketplaceBrainApi.reasoning(id)); setTab('recommendations'); }
    catch (e) { setError(e instanceof Error ? e.message : 'Reasoning failed'); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Executive' }, { id: 'recommendations', label: 'Recommendations' },
    { id: 'brief', label: 'Morning Brief' }, { id: 'conversation', label: 'AI Advisor' },
    { id: 'opportunities', label: 'Opportunities' }, { id: 'context', label: 'Context' },
    { id: 'learning', label: 'Learning' }, { id: 'knowledge', label: 'Knowledge' }, { id: 'timeline', label: 'Timeline' },
  ];

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main">
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>DEV-000110 · Marketplace Operating Brain · Observe → Decide → Learn</p>
            <h1 className="text-xl font-semibold">Executive Intelligence Workspace</h1>
            {dashboard && (
              <div className={`mt-2 flex flex-wrap gap-x-4 text-sm ${muted}`}>
                <span>{dashboard.recommendation_count} prioritized recommendations</span>
                <span>Profit opp <strong className={accent}>{dashboard.expected_profit_opportunity}</strong></span>
                <span>Recovery <strong className={accent}>{dashboard.recovery_opportunities}</strong></span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/dashboard" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Command Center</Link>
            <Link to="/platform/marketplace-operating-brain-dev110" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Sprint Center</Link>
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>{dark ? 'Light' : 'Dark'}</button>
          </div>
        </div>
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-4 ${card}`}>
            <p className="text-sm">{dashboard.today_summary}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>Highest Priority</p>
              <p className={`mt-1 font-semibold ${accent}`}>{dashboard.highest_priority}</p>
            </div>
            <div className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>Highest Risk</p>
              <p className="mt-1 font-semibold text-red-600">{dashboard.highest_risk}</p>
            </div>
          </div>
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-3">
          {loading ? <div className="h-20 animate-pulse rounded-xl bg-slate-200" /> : recommendations.map((r) => (
            <div key={r.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">#{r.priority} · {r.priority_score.toFixed(1)}</span>
                  <h3 className="mt-1 font-semibold">{r.title}</h3>
                  <p className={`text-sm ${muted}`}>{r.business_problem}</p>
                  {r.dna_evidence && <span className={`text-xs ${accent}`}>✓ DNA + cross-engine evidence</span>}
                  <p className={`mt-1 text-xs ${muted}`}>{r.expected_profit ?? r.expected_revenue} · {(r.confidence * 100).toFixed(0)}% confidence</p>
                  <p className={`text-xs ${accent}`}>{r.source_engines?.join(' · ')}</p>
                </div>
                <button type="button" onClick={() => showReasoning(r.id)} className="shrink-0 rounded-lg border px-2 py-1 text-xs">Explain</button>
              </div>
            </div>
          ))}
          {reasoning && (
            <div className={`rounded-xl border border-indigo-300 p-4 ${card}`}>
              <h3 className="font-semibold">Reasoning</h3>
              <p className={`mt-1 text-sm ${muted}`}><strong>Why:</strong> {reasoning.why}</p>
              <p className={`text-sm ${muted}`}><strong>Why now:</strong> {reasoning.why_now}</p>
              {reasoning.why_not && <p className={`text-sm ${muted}`}><strong>Why not alternatives:</strong> {reasoning.why_not}</p>}
            </div>
          )}
        </div>
      )}

      {tab === 'brief' && brief && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <h2 className="text-lg font-semibold">{brief.title}</h2>
          {brief.achievements && <ul className={`mt-2 list-disc pl-5 text-sm ${muted}`}>{brief.achievements.map((a) => <li key={a}>{a}</li>)}</ul>}
          {brief.risks && <><p className="mt-3 font-medium text-red-600">Risks</p><ul className={`list-disc pl-5 text-sm ${muted}`}>{brief.risks.map((r) => <li key={r}>{r}</li>)}</ul></>}
          {brief.recommended_actions && <><p className={`mt-3 font-medium ${accent}`}>Recommended Actions</p><ul className={`list-disc pl-5 text-sm ${muted}`}>{brief.recommended_actions.map((a) => <li key={a}>{a}</li>)}</ul></>}
          <p className={`mt-3 text-sm font-semibold ${accent}`}>{brief.expected_impact}</p>
        </div>
      )}

      {tab === 'conversation' && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <div className="flex gap-2">
            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} className="flex-1 rounded-lg border px-3 py-2 text-sm" placeholder="Ask the Operating Brain..." />
            <button type="button" disabled={asking} onClick={ask} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-50">{asking ? 'Thinking…' : 'Ask'}</button>
          </div>
          {conversation && (
            <div className="mt-4 space-y-2">
              <p className={`text-sm ${muted}`}>You: {conversation.query}</p>
              <p className="text-sm">{conversation.response}</p>
              {conversation.suggested_actions && <ul className={`list-disc pl-5 text-xs ${accent}`}>{conversation.suggested_actions.map((a) => <li key={a}>{a}</li>)}</ul>}
            </div>
          )}
        </div>
      )}

      {tab === 'opportunities' && opportunities.map((o) => (
        <div key={o.id} className={`rounded-xl border p-3 ${card}`}>
          <p className="font-medium">{o.title}</p>
          <p className={`text-xs ${muted}`}>{o.type} · {o.expected_profit ?? o.expected_revenue} · {o.time_sensitivity}</p>
        </div>
      ))}

      {tab === 'context' && context && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className={`text-sm ${muted}`}>DNA freshness: {context.dna_freshness} · Complete: {String(context.complete)}</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {context.engine_signals && Object.entries(context.engine_signals).map(([k, v]) => (
              <div key={k} className={`rounded-lg border p-2 text-sm ${card}`}><span className="capitalize font-medium">{k}</span>: {v}</div>
            ))}
          </div>
          {context.active_goals && <ul className={`mt-3 list-disc pl-5 text-sm ${muted}`}>{context.active_goals.map((g) => <li key={g}>{g}</li>)}</ul>}
        </div>
      )}

      {tab === 'learning' && learning.map((l) => (
        <div key={l.id} className={`rounded-xl border p-3 ${card}`}>
          <p className="font-medium">{l.target}</p>
          <p className={`text-sm ${muted}`}>{l.description} · {l.impact}</p>
        </div>
      ))}

      {tab === 'knowledge' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {knowledge.map((n) => (
            <div key={n.id} className={`rounded-xl border p-3 ${card}`}>
              <p className="font-medium">{n.label}</p>
              <p className={`text-xs ${muted}`}>{n.type} · {n.relationships?.join(', ')}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'timeline' && timeline.map((e) => (
        <div key={e.id} className={`flex gap-3 border-b py-2 ${card}`}>
          <time className={`shrink-0 text-xs ${muted}`}>{new Date(e.timestamp).toLocaleString()}</time>
          <span className="text-sm">{e.title}</span>
        </div>
      ))}
    </div>
  );
}
